import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";

export const AuthShell = ({ title, subtitle, children, footer }) => (
  <div
    className="min-h-screen flex items-center justify-center p-4 text-white relative overflow-hidden"
    style={{ background: "#0f1117" }}
  >
    {/* Background glows */}
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-emerald-500/8 blur-[140px]" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-blue-600/6 blur-[120px]" />
      <div className="fixed inset-0 grid-pattern opacity-[.15]" />
    </div>

    {/* Card */}
    <div
      className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
      style={{
        background: "#0D1B2E",
        border: "1px solid rgba(255,255,255,.08)",
        boxShadow: "0 24px 80px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.04)",
      }}
    >
      {/* Card header */}
      <div
        className="px-7 pt-7 pb-5"
        style={{ borderBottom: "1px solid rgba(255,255,255,.06)" }}
      >
        <Link to="/" className="flex items-center gap-2.5 mb-5" data-testid="auth-logo">
          <img src="/monyx-bot-hero.png" alt="Monyxx" className="h-9 w-9 object-contain" />
          <div className="font-display text-xl font-extrabold">
            <span className="text-emerald-400">M</span>
            <span className="text-white">ony</span>
            <span className="text-blue-400">x</span>
            <span className="ml-2 text-[9px] tracking-[0.3em] font-bold text-emerald-400/60">FINANCE</span>
          </div>
        </Link>
        <h1 className="font-display text-2xl font-bold text-white">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
      </div>

      {/* Card body */}
      <div className="px-7 py-6">{children}</div>

      {/* Footer */}
      {footer && (
        <div
          className="px-7 pb-6 text-center text-sm text-slate-400"
          style={{ borderTop: "1px solid rgba(255,255,255,.05)" }}
        >
          <div className="pt-4">{footer}</div>
        </div>
      )}
    </div>
  </div>
);

export const GoogleButton = ({ label = "Continuar com Google" }) => {
  const { googleLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleGoogle = async () => {
    setLoading(true);
    try {
      await googleLogin();
      toast.success("Bem-vindo!");
      navigate(searchParams.get("redirect") || "/app", { replace: true });
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        console.error("Erro detalhado do Firebase Auth:", err);
        toast.error("Erro: " + (err.message || "Falha ao entrar com Google"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleGoogle}
      disabled={loading}
      className="w-full h-11 rounded-full bg-white text-slate-900 font-semibold text-sm flex items-center justify-center gap-3 hover:bg-slate-100 transition disabled:opacity-50"
      data-testid="btn-google"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4">
        <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
        <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {loading ? "Aguarde..." : label}
    </button>
  );
};

export default function Login() {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: searchParams.get("email") || "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Bem-vindo de volta!");
      navigate(searchParams.get("redirect") || "/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = form.email.trim();
    if (!email) {
      toast.error("Preencha o campo de e-mail primeiro.");
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success(`E-mail de redefinição enviado para ${email}`);
    } catch (err) {
      const msg = err.code === "auth/user-not-found"
        ? "Nenhuma conta encontrada com esse e-mail."
        : err.code === "auth/invalid-email"
        ? "E-mail inválido."
        : "Erro ao enviar e-mail. Tente novamente.";
      toast.error(msg);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <AuthShell
      title="Entrar"
      subtitle="Acesse seu painel financeiro"
      footer={<>Não tem conta? <Link to="/register" className="text-emerald-300 hover:underline">Criar grátis</Link></>}
    >
      <GoogleButton />
      <div className="my-5 flex items-center gap-3 text-xs text-slate-500">
        <div className="flex-1 h-px bg-slate-800" /> ou com e-mail <div className="flex-1 h-px bg-slate-800" />
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Input
          type="email"
          required
          placeholder="seu@email.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="login-email"
        />
        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            required
            placeholder="Sua senha"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="pr-10"
            data-testid="login-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition"
            tabIndex={-1}
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <div className="text-right">
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={resetLoading}
            className="text-xs text-slate-400 hover:text-emerald-400 transition disabled:opacity-50"
          >
            {resetLoading ? "Enviando..." : "Esqueci minha senha"}
          </button>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-11 monyx-btn-primary text-white rounded-full font-semibold" data-testid="login-submit">
          {loading ? "Entrando..." : "Entrar"}
        </Button>
      </form>
    </AuthShell>
  );
}
